import { ViewConfig } from '../../../models/view-config.interface';
import { CarrierDeliveryNotesPaginatedService } from '@app/carrier/services/carrier-delivery-notes-paginated.service';
import { AppStore } from '@app/stores/appStore';
import { CarrierViewActionsService } from '../carrier-view-actions.service';
import { ColumnCreator } from '../column-creator';

export interface SharePermissionsDeliveryNotesConfigDeps {
    carrierDeliveryNotesPaginatedService: CarrierDeliveryNotesPaginatedService;
    actionsService: CarrierViewActionsService;
    appStore: InstanceType<typeof AppStore>;
}

export class SharePermissionsDeliveryNotesConfig {
    static create(deps: SharePermissionsDeliveryNotesConfigDeps): ViewConfig {
        return {
            id: 'share-permissions-deliverynotes',
            name: 'Delivery Notes',
            translationKey: 'OUTGOING-GOODS.TOGGLER.DELIVERY-NOTES',
            type: 'deliverynotes',
            userRole: 'carrier',
            apiConfig: {
                loadData: () => {
                    const orgSiteKey = deps.appStore.currentOrganizationSiteKey();
                    if (!orgSiteKey) {
                        return Promise.resolve();
                    }
                    // Use the paginated service that loads data into stores (same as regular carrier config)
                    return deps.carrierDeliveryNotesPaginatedService.loadCarrierDeliveryNotesPaginated(
                        orgSiteKey
                    );
                },
            },
            tableConfig: {
                columns: [
                    ColumnCreator.select(),
                    ColumnCreator.deliveryNoteNumber({
                        header: 'SPEDITEUR.TABLE.HEADERS.NOTE-NUMBER',
                    }),
                    ColumnCreator.orderNumber(),
                    ColumnCreator.deliveryNoteBundleNumber({
                        header: 'SPEDITEUR.TABLE.HEADERS.BUNDLE-KEY',
                    }),
                    ColumnCreator.consignorName({
                        header: 'SPEDITEUR.TABLE.HEADERS.CONSIGNOR',
                    }),
                    ColumnCreator.consigneeName({
                        header: 'SPEDITEUR.TABLE.HEADERS.CONSIGNEE',
                    }),
                    ColumnCreator.carrierName({
                        header: 'SPEDITEUR.TABLE.HEADERS.CARRIER',
                    }),
                    ColumnCreator.dateShipped({
                        header: 'SPEDITEUR.TABLE.HEADERS.SENT-DATE',
                    }),
                    ColumnCreator.status({
                        key: 'deliveryNoteBundleStatus',
                        header: 'SPEDITEUR.TABLE.HEADERS.STATUS',
                    }),
                ],
                features: {
                    sortable: true,
                    filterable: true,
                    paginated: false, // No pagination for share permissions dialog
                    selectable: true,
                    expandable: false,
                },
            },
            filterConfig: {
                filterKeys: [
                    {
                        keyName: 'number',
                        label: 'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.DELIVERY-NOTE-NUMBER',
                        translationKey:
                            'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.DELIVERY-NOTE-NUMBER',
                    },
                    {
                        keyName: 'orderNumber',
                        label: 'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.ORDER-NO',
                        translationKey:
                            'OUTGOING-GOODS.DELIVERY-NOTE-LIST-VIEW.TABLE-HEADER.ORDER-NO',
                    },
                    {
                        keyName: 'consignorName',
                        label: 'SPEDITEUR.TABLE.HEADERS.CONSIGNOR',
                        translationKey: 'SPEDITEUR.TABLE.HEADERS.CONSIGNOR',
                    },
                    {
                        keyName: 'consigneeName',
                        label: 'SPEDITEUR.TABLE.HEADERS.CONSIGNEE',
                        translationKey: 'SPEDITEUR.TABLE.HEADERS.CONSIGNEE',
                    },
                    {
                        keyName: 'carrierName',
                        label: 'SPEDITEUR.TABLE.HEADERS.CARRIER',
                        translationKey: 'SPEDITEUR.TABLE.HEADERS.CARRIER',
                    },
                    {
                        keyName: 'deliveryNoteBundleStatus',
                        label: 'SPEDITEUR.TABLE.HEADERS.STATUS',
                        translationKey: 'SPEDITEUR.TABLE.HEADERS.STATUS',
                    },
                    {
                        keyName: 'deliveryNoteBundleNumber',
                        label: 'SPEDITEUR.TABLE.HEADERS.BUNDLE-KEY',
                        translationKey: 'SPEDITEUR.TABLE.HEADERS.BUNDLE-KEY',
                    },
                ],
                showClosed: false,
                enableAdvancedFilters: true,
            },
            searchConfig: {
                validSortFields: [
                    'dateCreated',
                    'number',
                    'orderNumber',
                    'consignorName',
                    'consigneeName',
                    'carrierName',
                    'deliveryNoteBundleNumber',
                ],
                defaultSortField: 'dateCreated',
                defaultSortDirection: 'desc',
            },
            permissions: {
                canCreate: false,
                canUpdate: false,
                canDelete: false,
                canExport: false,
                canImport: false,
            },
            selectionConfig: {
                clearOnViewTypeSwitch: true,
            },
        };
    }
}
